import {
  type ChangeEvent,
  type ReactElement,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import { formatIsoDateTime } from './groupWatchRuns';
import {
  elevatedCardsInWindow,
  feedFilterOptions,
  filterIncidentCards,
  filterIncidentCardsByFoundDays,
  groupIncidentCards,
  mockPulseNow,
  pulseKpis,
  watchTowerRunHref,
} from './marketingOpsSelectors';
import {
  AFFECTED_STATUSES,
  findingStoryHref,
  opportunityRows,
  setAffectedLinkStatus,
} from './marketingOpsStory';
import {
  clampTimelineRange,
  filterCardsByFoundDays,
  fullTimelineRange,
  issueMapMarkers,
  issueMarkCounts,
  runIssueCardsForFindings,
  severityAgentCounts,
  severityHeatRows,
  timelineDaysEndingAt,
} from './runAnalysisSelectors';
import {
  TargetedMarketingGate,
  TargetedMarketingHeader,
} from './TargetedMarketingChrome';
import useWatchTowerMock from './useWatchTowerMock';
import WatchTowerIssueOverview from './WatchTowerIssueOverview';
import { formatAgentType } from './watchTowerSelectors';

import type {
  IncidentCard,
  PulseWindowDays,
  RiskGroupBy,
} from './marketingOpsSelectors';
import type { RunIssueCard, TimelineRange } from './runAnalysisSelectors';

import styles from './scss/TargetedMarketing.module.scss';

function severityClass(severity: string): string {
  if (severity === 'high' || severity === 'critical') {
    return styles.statusFailed;
  }
  return styles.statusDraft;
}

function MarketingOps(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    state, update, liveActions,
  } = useWatchTowerMock();
  const [windowDays, setWindowDays] = useState<PulseWindowDays>(7);
  const [severity, setSeverity] = useState('');
  const [agentType, setAgentType] = useState('');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const [hasAffected, setHasAffected] = useState<'' | 'yes' | 'no'>('');
  const [groupBy, setGroupBy] = useState<RiskGroupBy>('company');
  const [timelineRange, setTimelineRange] = useState<TimelineRange>([0, 6]);

  const windowCards = useMemo(
    () => elevatedCardsInWindow(state, windowDays),
    [state, windowDays],
  );
  const kpis = useMemo(() => pulseKpis(windowCards), [windowCards]);
  const timelineDays = useMemo(
    () => timelineDaysEndingAt(mockPulseNow(), windowDays),
    [windowDays],
  );
  const clampedRange = clampTimelineRange(timelineRange, timelineDays);
  const startDay = timelineDays[clampedRange[0]] || '';
  const endDay = timelineDays[clampedRange[1]] || '';
  const rangedCards = useMemo(
    () => filterIncidentCardsByFoundDays(windowCards, startDay, endDay),
    [endDay, startDay, windowCards],
  );
  const windowIssues = useMemo(
    () => runIssueCardsForFindings(state, windowCards.map((card) => card.finding)),
    [state, windowCards],
  );
  const rangedIssues = useMemo(
    () => filterCardsByFoundDays(windowIssues, startDay, endDay),
    [endDay, startDay, windowIssues],
  );
  const markers = useMemo(() => issueMapMarkers(rangedIssues), [rangedIssues]);
  const markCounts = useMemo(() => issueMarkCounts(windowIssues), [windowIssues]);
  const severityRows = useMemo(() => severityHeatRows(rangedIssues), [rangedIssues]);
  const severityHeat = useMemo(() => severityAgentCounts(rangedIssues), [rangedIssues]);
  const filteredCards = useMemo(
    () => filterIncidentCards(rangedCards, {
      severity, agentType, country, industry, hasAffected,
    }),
    [agentType, country, hasAffected, industry, rangedCards, severity],
  );
  const options = useMemo(() => feedFilterOptions(rangedCards), [rangedCards]);
  const riskGroups = useMemo(
    () => groupIncidentCards(filteredCards, groupBy),
    [filteredCards, groupBy],
  );

  const onWindowChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const days = Number(event.target.value) as PulseWindowDays;
    setWindowDays(days);
    setTimelineRange([0, days - 1]);
  };

  const onMapCountryClick = (cellCountry: string) => {
    if (country === cellCountry) {
      setCountry('');
      return;
    }
    setCountry(cellCountry);
  };

  const onTimelineMarkClick = (day: string) => {
    const index = timelineDays.indexOf(day);
    if (index < 0) {
      return;
    }
    if (clampedRange[0] === index && clampedRange[1] === index) {
      setTimelineRange(fullTimelineRange(timelineDays));
      return;
    }
    setTimelineRange([index, index]);
  };

  const onSeverityAgentClick = (rowSeverity: string, cellAgent: string) => {
    if (severity === rowSeverity && agentType === cellAgent) {
      setSeverity('');
      setAgentType('');
      return;
    }
    setSeverity(rowSeverity);
    setAgentType(cellAgent);
  };

  const onOpenPulseIssue = (card: RunIssueCard) => {
    navigate(generatePath(AppRoutes.marketingOpsFinding, {
      publicId: card.finding.publicId,
    }));
  };

  const opportunities = useMemo(() => opportunityRows(state), [state]);

  const onDigest = async () => {
    try {
      await liveActions.sendDigest();
      toast.success(t('targetedMarketing.digestSentToast'));
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const onOpportunityStatus = (linkId: number, status: string) => {
    update((prev) => setAffectedLinkStatus(prev, linkId, status));
    toast.success(t('targetedMarketing.statusSaved'));
  };

  const openStory = (card: IncidentCard) => {
    navigate(generatePath(AppRoutes.marketingOpsFinding, {
      publicId: card.finding.publicId,
    }));
  };

  const openWatchTower = (card: IncidentCard) => {
    if (!card.run) {
      return;
    }
    navigate(watchTowerRunHref(card.run, card.finding.publicId));
  };

  return (
    <TargetedMarketingGate>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <TargetedMarketingHeader
            title={t('targetedMarketing.marketingOpsTitle')}
            backTo={AppRoutes.agentsManagement}
            backLabel={t('common.back')}
            actions={(
              <Button
                title={t('targetedMarketing.runDigest')}
                variant={BUTTON_VARIANT.secondary}
                onClick={onDigest}
              />
            )}
          />

          <Panel className={styles.panel}>
            <div className={styles.toolbar}>
              <h2 className={styles.sectionTitle}>{t('targetedMarketing.pulseTitle')}</h2>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.pulseWindow')}
                value={windowDays}
                onChange={onWindowChange}
              >
                <option value={7}>{t('targetedMarketing.window7')}</option>
                <option value={30}>{t('targetedMarketing.window30')}</option>
              </select>
            </div>
            <p className={styles.muted}>{t('targetedMarketing.pulseMockToday')}</p>
            <div className={styles.kpiRow}>
              <article className={styles.kpiCard}>
                <p className={styles.kpiLabel}>{t('targetedMarketing.kpiElevated')}</p>
                <p className={styles.kpiValue}>{kpis.elevatedCount}</p>
              </article>
              <article className={styles.kpiCard}>
                <p className={styles.kpiLabel}>{t('targetedMarketing.kpiCompanies')}</p>
                <p className={styles.kpiValue}>{kpis.companiesWithAlerts}</p>
              </article>
              <article className={styles.kpiCard}>
                <p className={styles.kpiLabel}>{t('targetedMarketing.kpiOpportunities')}</p>
                <p className={styles.kpiValue}>{kpis.newOpportunities}</p>
              </article>
              <article className={styles.kpiCard}>
                <p className={styles.kpiLabel}>{t('targetedMarketing.kpiAgentMix')}</p>
                {kpis.agentMix.length === 0 ? (
                  <p className={styles.muted}>{t('targetedMarketing.none')}</p>
                ) : (
                  <ul className={styles.kpiMix}>
                    {kpis.agentMix.map((item) => (
                      <li key={item.findingType}>
                        {item.findingType}
                        {': '}
                        {item.count}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </div>

            <WatchTowerIssueOverview
              agentType={agentType}
              country={country}
              severity={severity}
              markers={markers}
              timelineDays={timelineDays}
              timelineRange={clampedRange}
              markCounts={markCounts}
              severityRows={severityRows}
              severityAgentCounts={severityHeat}
              afterTimeline={null}
              emptyHeatText={t('targetedMarketing.emptyPulse')}
              mapPanelId="marketing-ops-issue-map"
              heatPanelId="marketing-ops-heat"
              onMapCountryClick={onMapCountryClick}
              onTimelineRangeChange={setTimelineRange}
              onTimelineMarkClick={onTimelineMarkClick}
              onSeverityAgentClick={onSeverityAgentClick}
              onOpenIssue={onOpenPulseIssue}
            />
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.feedTitle')}</h2>
            <div className={styles.filters}>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.severity')}
                value={severity}
                onChange={(event) => setSeverity(event.target.value)}
              >
                <option value="">{t('targetedMarketing.allSeverities')}</option>
                <option value="critical">critical</option>
                <option value="high">high</option>
              </select>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.selectAgentType')}
                value={agentType}
                onChange={(event) => setAgentType(event.target.value)}
              >
                <option value="">{t('targetedMarketing.allAgents')}</option>
                {options.agentTypes.map((item) => (
                  <option key={item} value={item}>{formatAgentType(item)}</option>
                ))}
              </select>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.country')}
                value={country}
                onChange={(event) => setCountry(event.target.value)}
              >
                <option value="">{t('targetedMarketing.allCountries')}</option>
                {options.countries.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.industry')}
                value={industry}
                onChange={(event) => setIndustry(event.target.value)}
              >
                <option value="">{t('targetedMarketing.allIndustries')}</option>
                {options.industries.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.hasAffected')}
                value={hasAffected}
                onChange={(event) => setHasAffected(event.target.value as '' | 'yes' | 'no')}
              >
                <option value="">{t('targetedMarketing.hasAffectedAny')}</option>
                <option value="yes">{t('targetedMarketing.hasAffectedYes')}</option>
                <option value="no">{t('targetedMarketing.hasAffectedNo')}</option>
              </select>
            </div>
            {filteredCards.length === 0 ? (
              <EmptyList text={t('targetedMarketing.emptyFeed')} />
            ) : (
              <div className={styles.feedGrid}>
                {filteredCards.map((card) => (
                  <article key={card.finding.publicId} className={styles.incidentCard}>
                    <span className={`${styles.statusPill} ${severityClass(card.finding.severity)}`}>
                      {card.finding.severity}
                    </span>
                    <p className={styles.incidentMeta}>
                      {card.companyName}
                      {' · '}
                      {card.country || t('targetedMarketing.none')}
                      {' · '}
                      {card.agentLabel}
                    </p>
                    <h3 className={styles.incidentTitle}>{card.finding.title}</h3>
                    <p className={styles.muted}>{formatIsoDateTime(card.finding.observedAt)}</p>
                    <p className={styles.incidentImpact}>{card.impactLine}</p>
                    <div className={styles.actions}>
                      <Button
                        title={t('targetedMarketing.openStory')}
                        variant={BUTTON_VARIANT.secondary}
                        onClick={() => openStory(card)}
                      />
                      <Button
                        title={t('targetedMarketing.openInWatchTower')}
                        variant={BUTTON_VARIANT.secondary}
                        disabled={!card.run}
                        onClick={() => openWatchTower(card)}
                      />
                    </div>
                  </article>
                ))}
              </div>
            )}
          </Panel>

          <Panel className={styles.panel}>
            <div className={styles.toolbar}>
              <h2 className={styles.sectionTitle}>{t('targetedMarketing.riskTitle')}</h2>
              <select
                className={styles.filterNative}
                aria-label={t('targetedMarketing.groupBy')}
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as RiskGroupBy)}
              >
                <option value="company">{t('targetedMarketing.groupCompany')}</option>
                <option value="industry">{t('targetedMarketing.groupIndustry')}</option>
                <option value="country">{t('targetedMarketing.groupCountry')}</option>
                <option value="agent">{t('targetedMarketing.groupAgent')}</option>
              </select>
            </div>
            {riskGroups.length === 0 ? (
              <EmptyList text={t('targetedMarketing.emptyFeed')} />
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('targetedMarketing.groupBy')}</th>
                      <th>{t('targetedMarketing.openFindings')}</th>
                      <th>{t('targetedMarketing.title')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {riskGroups.map((group) => (
                      <tr key={group.key}>
                        <td>{group.label}</td>
                        <td>{group.count}</td>
                        <td>
                          {group.cards.map((card) => card.finding.title).join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.opportunityTitle')}</h2>
            {opportunities.length === 0 ? (
              <EmptyList text={t('targetedMarketing.emptyOpportunity')} />
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('targetedMarketing.issue')}</th>
                      <th>{t('targetedMarketing.sourceCompany')}</th>
                      <th>{t('targetedMarketing.affectedCompany')}</th>
                      <th>{t('targetedMarketing.relation')}</th>
                      <th>{t('targetedMarketing.products')}</th>
                      <th>{t('targetedMarketing.status')}</th>
                      <th>{t('targetedMarketing.emailCol')}</th>
                      <th>{t('targetedMarketing.flaggedBy')}</th>
                      <th>{t('targetedMarketing.talkingPoint')}</th>
                      <th>{t('targetedMarketing.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opportunities.map((row) => (
                      <tr key={row.link.id}>
                        <td>
                          {row.finding ? (
                            <>
                              <span className={`${styles.statusPill} ${severityClass(row.finding.severity)}`}>
                                {row.finding.severity}
                              </span>
                              {' '}
                              {row.finding.title}
                            </>
                          ) : t('targetedMarketing.none')}
                        </td>
                        <td>{row.sourceName}</td>
                        <td>{row.affectedName}</td>
                        <td>{row.link.relationKind}</td>
                        <td>{row.productName || t('targetedMarketing.none')}</td>
                        <td>
                          <select
                            className={styles.filterNative}
                            aria-label={t('targetedMarketing.status')}
                            value={row.link.status}
                            onChange={(event) => onOpportunityStatus(row.link.id, event.target.value)}
                          >
                            {AFFECTED_STATUSES.map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {row.link.notifiedAt
                            ? formatIsoDateTime(row.link.notifiedAt)
                            : t('targetedMarketing.emailNotSent')}
                        </td>
                        <td>{row.link.flaggedBy}</td>
                        <td>{row.link.talkingPoint || t('targetedMarketing.none')}</td>
                        <td>
                          {row.finding ? (
                            <Button
                              title={t('targetedMarketing.openStory')}
                              variant={BUTTON_VARIANT.secondary}
                              onClick={() => {
                                const { finding } = row;
                                if (finding) {
                                  navigate(findingStoryHref(finding.publicId));
                                }
                              }}
                            />
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </TargetedMarketingGate>
  );
}

export default MarketingOps;
